import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ShieldCheck,
  BadgeCheck,
  Wallet,
  Truck,
  Ship,
  HeartHandshake,
  ArrowRight,
  ArrowDown,
  Check,
} from "lucide-react";
import { Reveal, MaskedLines, Overline } from "@/components/motion";
import Marquee from "@/components/Marquee";
import QuoteDialog from "@/components/QuoteDialog";
import Seo from "@/components/Seo";
import { PRODUCTS, IMAGES } from "@/data/products";

const WHY = [
  { icon: ShieldCheck, title: "Trusted Procurement Network", desc: "Direct sourcing from established farmer networks across India's millet belt." },
  { icon: BadgeCheck, title: "Consistent Quality", desc: "Machine cleaning, grading and inspection ensure uniform, export-grade output." },
  { icon: Wallet, title: "Competitive Pricing", desc: "Efficient sourcing and scale translate into fair, transparent pricing." },
  { icon: Truck, title: "Timely Delivery", desc: "Reliable logistics and documentation for on-schedule dispatch." },
  { icon: Ship, title: "Export Ready", desc: "Compliant packaging and paperwork for smooth international shipments." },
  { icon: HeartHandshake, title: "Customer First Approach", desc: "Long-term partnerships built on responsiveness and trust." },
];

const PROCESS = [
  { n: "01", t: "Trusted Procurement & Receiving", d: "We source pearl millet directly from trusted farmers and verified procurement partners across India's major millet-growing regions. Every lot is received with complete traceability to ensure consistency, authenticity and quality from the source." },
  { n: "02", t: "Storage & Initial Quality Inspection", d: "Incoming grain is stored in clean, pest-controlled warehouses and undergoes comprehensive inspection for moisture, foreign matter, grain maturity, physical appearance and overall quality. Only approved lots proceed to processing." },
  { n: "03", t: "Cleaning & De-Stoning", d: "Advanced pre-cleaning and de-stoning systems remove dust, husk, straw, stones and other contaminants, ensuring a clean and safe raw material for further processing." },
  { n: "04", t: "Precision Processing & Grading", d: "Using advanced separation, gravity and grading equipment, grains are sorted by size, density and quality. Lightweight kernels, broken and immature grains, and remaining impurities are removed to produce premium export-grade millet." },
  { n: "05", t: "Hygienic Packaging & Storage", d: "Processed millet is packed using food-grade packaging materials and stored in clean, dry, pest-controlled warehouses to preserve freshness and product integrity until dispatch." },
  { n: "06", t: "Logistics & Export", d: "Finished products are transported to port, professionally loaded into export containers, sealed, inspected and shipped with complete export documentation — ensuring safe and timely global delivery." },
];

const TESTED_FOR = [
  "Purity",
  "Moisture Content",
  "Foreign Matter",
  "Damaged & Broken Grains",
  "Grain Size Uniformity",
  "Packaging Integrity",
  "Weight Accuracy",
  "Export Documentation Compliance",
];

const FAQ_ITEMS = [
  {
    q: "Are you a bulk millet exporter from India?",
    a: "Yes. SDRC Global supplies bulk machine-clean millet, bajra (pearl millet), wheat and sesbania seed to buyers in India and global markets with a focus on consistent quality and export-ready handling.",
  },
  {
    q: "Do you export bajra and pearl millet to international buyers?",
    a: "Yes. We support international buyers looking for premium-grade bajra and pearl millet with quality checks, secure packaging and export documentation suitable for overseas trade.",
  },
  {
    q: "Can you supply wheat and millet for feed and food processing industries?",
    a: "Yes. We serve food processors, feed manufacturers, agri traders and importers requiring consistent, export-grade agricultural commodities with dependable supply and documentation.",
  },
  {
    q: "Do you support bulk commercial quantities?",
    a: "Yes. Our focus is on bulk export supply, commercially sized lots and long-term trade relationships for Indian agricultural products.",
  },
  {
    q: "Where do you export from India?",
    a: "We serve buyers across the Middle East, Africa, Southeast Asia and other international markets, while also supporting domestic buyers seeking reliable sourcing from India.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.75]);

  return (
    <div className="relative">
      <Seo
        title="Indian Millet & Grain Exporter"
        description="SDRC Global is an Indian agricultural commodity exporter supplying pearl millet, green millet, wheat and sesbania seed for international food, feed and trading buyers."
        path="/"
        image={IMAGES.heroField}
        jsonLd={[]}
      />
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero">
        <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
          <img src={IMAGES.heroField} alt="Millet fields at harvest" fetchPriority="high" decoding="async" sizes="100vw" className="w-full h-full object-cover" />
        </motion.div>
        <motion.div className="absolute inset-0 bg-brand-green-dark" style={{ opacity: overlayOpacity }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

        <div className="relative z-10 max-w-[1400px] mx-auto w-full px-5 md:px-10 pb-20 md:pb-28">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <Overline className="text-brand-gold-light mb-6">From Indian Farms to Global Markets</Overline>
          </motion.div>
          <h1 className="font-display text-brand-cream font-medium tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-8xl max-w-5xl">
            <MaskedLines
              lines={["Indian Millet & Agricultural", "Commodity Exporter"]}
              delay={0.35}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 text-lg md:text-xl text-brand-cream/85 max-w-2xl leading-relaxed"
          >
            Trusted trader delivering high-quality agricultural products to domestic and international buyers.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <QuoteDialog>
              <button data-testid="hero-quote-btn" className="group inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-green-dark px-8 py-4 rounded-full font-semibold tracking-wide hover:bg-brand-gold-light transition-colors duration-300">
                Request a Quote
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </QuoteDialog>
            <Link
              to="/products"
              data-testid="hero-products-btn"
              className="inline-flex items-center justify-center gap-2 border border-brand-cream/40 text-brand-cream px-8 py-4 rounded-full font-medium tracking-wide hover:bg-brand-cream hover:text-brand-green-dark transition-colors duration-300"
            >
              View Products
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-8 right-8 hidden md:flex items-center gap-2 text-brand-cream/70 text-xs uppercase tracking-[0.2em]"
        >
          Scroll <ArrowDown size={14} className="animate-bounce" />
        </motion.div>
      </section>

      {/* ABOUT INTRO */}
      <section className="relative z-10 bg-brand-cream py-24 md:py-36" data-testid="about-intro">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <Reveal><Overline className="text-brand-gold-dark mb-6">About SDRC Global</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-brand-green leading-[1.05] text-balance">
                An export division built on decades of grain-trade expertise.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p className="text-lg text-brand-charcoal leading-relaxed">
                SDRC Global is the international export arm of a trusted name in the grain trade. We combine deep expertise in grain sourcing with a reliable supplier network and a relentless focus on quality-driven processing.
              </p>
              <ul className="mt-8 space-y-4">
                {["Expertise in grain sourcing", "Trusted supplier network", "Quality-focused processing", "Reliable deliveries", "International export vision"].map((t) => (
                  <li key={t} className="flex items-center gap-4 border-b border-brand-line pb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-gold" />
                    <span className="text-brand-charcoal font-medium">{t}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-brand-green font-semibold hover:gap-3 transition-all">
                Our full story <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="bg-brand-cream pb-24 md:pb-32" data-testid="home-products">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <Reveal><Overline className="text-brand-gold-dark mb-5">Our Products</Overline></Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-brand-green">
                  Premium grades, export ready.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <Link to="/products" className="inline-flex items-center gap-2 text-brand-green font-semibold hover:gap-3 transition-all">
                Explore all products <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <div className="group bg-white border border-brand-line hover:border-brand-gold transition-colors duration-500 h-full flex flex-col" data-testid={`product-card-${p.slug}`}>
                  <Link to={`/products/${p.slug}`} className="overflow-hidden aspect-[4/5] block" aria-label={`View ${p.name} details`}>
                    <img src={p.image} alt={p.imageAlt || p.name} loading="lazy" decoding="async" sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </Link>
                  <div className="p-7 flex flex-col flex-1">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-brand-gold-dark font-semibold">{p.tagline}</span>
                    <h3 className="font-display text-2xl font-medium text-brand-green mt-2">
                      <Link to={`/products/${p.slug}`} className="hover:text-brand-gold-dark transition-colors">{p.name}</Link>
                    </h3>
                    <p className="text-sm text-brand-muted mt-3 leading-relaxed flex-1">{p.short}</p>
                    <div className="flex items-center justify-between mt-6 pt-5 border-t border-brand-line">
                      <QuoteDialog product={p.name}>
                        <button data-testid={`card-quote-${p.slug}`} className="text-sm font-semibold text-brand-green hover:text-brand-gold-dark transition-colors inline-flex items-center gap-1.5">
                          Request Quote <ArrowRight size={14} />
                        </button>
                      </QuoteDialog>
                      <Link to={`/products/${p.slug}`} className="text-sm text-brand-muted hover:text-brand-green transition-colors">Details</Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee items={["Premium Millet", "Wheat & Bajra", "Global Export Standards", "Machine Cleaned", "Bulk Supply", "Trusted Since Generations"]} />

      {/* WHY CHOOSE */}
      <section className="bg-brand-cream py-24 md:py-32" data-testid="why-choose">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Overline className="text-brand-gold-dark mb-5">Why Choose SDRC Global</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-brand-green max-w-2xl">
              Every shipment reflects our commitment to quality.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-line mt-14 border border-brand-line">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 0.06}>
                <div className="bg-brand-cream p-9 h-full group hover:bg-white transition-colors duration-500">
                  <div className="w-12 h-12 rounded-full border border-brand-gold/40 flex items-center justify-center text-brand-green group-hover:bg-brand-green group-hover:text-white transition-colors duration-500">
                    <w.icon size={22} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-display text-2xl font-medium text-brand-green mt-6">{w.title}</h3>
                  <p className="text-sm text-brand-muted mt-3 leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESSING */}
      <section className="bg-white py-24 md:py-32 border-y border-brand-line" data-testid="processing">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Reveal><Overline className="text-brand-gold-dark mb-5">Processing &amp; Quality</Overline></Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-brand-green leading-tight">
                  Our Quality Assurance Process
                </h2>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="text-brand-muted mt-5 leading-relaxed">
                  Every batch follows a rigorous, quality-controlled process before reaching our customers. From procurement to container stuffing, we ensure consistency, cleanliness and export-ready standards at every stage.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 overflow-hidden rounded-sm">
                  <img src={IMAGES.harvestMacro} alt="Grain cleaning and packaging at SDRC Global" loading="lazy" decoding="async" sizes="(min-width: 1024px) 42vw, 100vw" className="w-full aspect-[4/3] object-cover" />
                </div>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="space-y-0">
              {PROCESS.map((p, i) => (
                <Reveal key={p.n} delay={i * 0.05}>
                  <div className="flex gap-6 py-7 border-b border-brand-line group">
                    <span className="font-display text-4xl text-brand-gold/60 group-hover:text-brand-gold transition-colors w-16 shrink-0">{p.n}</span>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl font-medium text-brand-green">{p.t}</h3>
                      <p className="text-brand-muted mt-1.5">{p.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* Every Shipment is Tested For */}
            <Reveal delay={0.1}>
              <div className="mt-12 bg-brand-cream border border-brand-line rounded-sm p-8" data-testid="tested-for">
                <h3 className="font-display text-2xl md:text-3xl font-medium text-brand-green">Every Shipment is Tested For</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-6">
                  {TESTED_FOR.map((item) => (
                    <div key={item} className="flex items-center gap-3 border-b border-brand-line/70 pb-3">
                      <span className="w-6 h-6 rounded-full bg-brand-green/10 flex items-center justify-center shrink-0">
                        <Check size={14} className="text-brand-green" strokeWidth={2.5} />
                      </span>
                      <span className="text-brand-charcoal font-medium text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Our Quality Commitment */}
            <Reveal delay={0.12}>
              <div className="mt-8 border-l-2 border-brand-gold pl-6 md:pl-8 py-2" data-testid="quality-commitment">
                <span className="text-xs uppercase tracking-[0.2em] text-brand-gold-dark font-semibold">Our Quality Commitment</span>
                <p className="font-display text-2xl md:text-3xl text-brand-green leading-snug mt-3 font-light">
                  Quality isn't inspected at the end — it is <span className="italic text-brand-gold-dark">built into every stage</span> of our process.
                </p>
                <p className="text-brand-muted mt-4 leading-relaxed">
                  At SDRC Global, every shipment reflects our commitment to food safety, consistency, traceability and long-term customer trust.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GLOBAL VISION (dark) */}
      <section className="relative bg-brand-green-dark py-28 md:py-40 overflow-hidden" data-testid="global-vision">
        <div className="absolute inset-0 opacity-20">
          <img src={IMAGES.logistics} alt="" aria-hidden="true" loading="lazy" decoding="async" sizes="100vw" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Overline className="text-brand-gold-light mb-6">Global Vision</Overline></Reveal>
          <Reveal delay={0.05}>
            <p className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-brand-cream max-w-4xl font-light">
              SDRC Global is committed to supplying <span className="text-brand-gold italic">premium Indian agricultural products</span> to international markets — with a strong focus on quality, transparency and long-term partnerships.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-cream py-20 md:py-28" data-testid="faq-section">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <Reveal><Overline className="text-brand-gold-dark mb-5">Export FAQs</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-brand-green max-w-2xl">
              Common buyer questions about millet and grain exports from India.
            </h2>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {FAQ_ITEMS.map((item, index) => (
              <Reveal key={item.q} delay={index * 0.06}>
                <div className="bg-white border border-brand-line rounded-sm p-6 h-full">
                  <h3 className="font-display text-2xl text-brand-green mb-3">{item.q}</h3>
                  <p className="text-brand-muted leading-relaxed">{item.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="bg-brand-cream py-24 md:py-32" data-testid="home-cta">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="bg-brand-green rounded-sm px-8 md:px-16 py-16 md:py-24 text-center relative overflow-hidden">
            <div className="relative z-10">
              <Reveal>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-brand-cream tracking-tight text-balance">
                  Looking for a Reliable Millet Supplier?
                </h2>
              </Reveal>
              <Reveal delay={0.05}>
                <p className="text-brand-cream/80 mt-5 text-lg">Request a quote today and partner with a supplier built on trust.</p>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                  <QuoteDialog>
                    <button data-testid="cta-quote-btn" className="bg-brand-gold text-brand-green-dark px-8 py-4 rounded-full font-semibold hover:bg-brand-gold-light transition-colors">
                      Request Quote
                    </button>
                  </QuoteDialog>
                  <Link to="/contact" data-testid="cta-contact-btn" className="border border-brand-cream/40 text-brand-cream px-8 py-4 rounded-full font-medium hover:bg-brand-cream hover:text-brand-green transition-colors">
                    Contact Us
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
